import {defineStore} from 'pinia';
// nextTick используется для того, чтобы выполнить операцию не сразу же, пока код ещё не отработал и vue не обновил DOM,
// а после обновления, когда в DOM-е выведутся актуальные данные
import {nextTick} from 'vue';
import { alertsStore } from './alerts';
import { useEventBus } from './eventBus';

export const basketStore = defineStore('basket', {
	state: () => ({
		_isDebugEnabled: false,
		_reloadInitiator: false,
		_BACKEND_BITRIX_CONTROLLER_NAME: 'o2k:sitecore.api.Basket',
		// загружен ли блок на текущий момент (не выполняются ли запросы на бэкенд или другие действия, его изменяющие)
		isLoaded: false,
		// не производится ли валидация при выходе с шага - нужен флаг чтобы блок корзины сворачивался во время выходной
		// ревалидации, иначе переключение выглядит слишком рваным
		isStepLeaveCalculationsInProgress: false,
		// можно ли оформить заказ на текущую корзину
		isOrderable: false,
		// проинициализирована ли корзина (была ли она загружена хотя бы раз)
		isInitialized: false,
		items: [],
		isAllItemsSelected: false,
		summary: {},

		discounts: {},
		coupons: {},

		_alertsStore: alertsStore(),
		eventBus: useEventBus(),
		
	}),
	getters: {
		// пуста ли корзина (! есть ли в корзине товары)
		isEmpty() {
			return this.items.length <= 0;
		},

		// сумма скидок
		basketDiscountsSum() {
			return Object.values(this.discounts).reduce((sum, el) => sum + el.discountAmount, 0);
		},

		itemsNotDelayed() {
			return this.items.filter((item) => !item.isDelayed);
		},

		itemsAvailable() {
			return this.items.filter((item) =>  item.isAvailable);
		},

		itemsActiveAvailable() {
			return this.itemsAvailable.filter((item) => !item.isDeleted);
		},

		itemsUnavailable() {
			return this.itemsNotDelayed.filter((item) => !item.isAvailable && !item.isDeleted);
		},
		
		itemsSelected() {
			return this.items.filter((item) => item.isSelected ?? false);
		},
		
		isAllSelected() {
			if (this.items.length === 0) return false;
			return this.items.every(item => item.isSelected);
		}
	},
	actions: {
		init() {
			this._reloadInitiator = 'init';
			this.eventBus.subscribe('o2k:orderStore:afterDataUpdate', this.handleOrderStorageUpdate.bind(this));
			this.eventBus.subscribe('o2k:basket:externalBasketDataUpdate', this.externalBasketDataUpdate.bind(this));
		},

		handleOrderStorageUpdate(event) {
			if (this._isDebugEnabled)
				console.log('handleOrderStorageUpdate initiator:' + event.data.initiator);

			if (event.data.initiator === 'basket')
				return;

			if (this._isDebugEnabled)
				console.log('handleOrderStorageUpdate run');

			this._reloadInitiator = 'order';
			if (event.data.orderData?.basketData) {
				this.setBasketData(event.data.orderData.basketData);
			} else {
				this.reloadBasketData();
			}

		},

		externalBasketDataUpdate(event) {
			let initiator = 'unknown';
			if (event.data.initiator) {
				initiator = event.data.initiator;
			}
			if (this._isDebugEnabled)
				console.log('externalBasketDataUpdate initiator:' + initiator);

			this._reloadInitiator = initiator;
			if (event.data.basketData) {
				this.setBasketData(event.data.basketData);
			} else {
				this.reloadBasketData();
			}
		},

		changeDebugStatus(debugStatus) {
			this._isDebugEnabled = debugStatus;
		},

		/**
		 * Переключает выбор товара в корзине
		 * 
		 * @param item object
		 * @param value boolean
		 */
		toggleSelectItem(item, value) {
			const found = this.items.find(i => i.basketId === item.basketId);
			if (found) {
				found.isSelected = value;
			}
		},

		/**
		 * Выбирает/снимает выбор со всех товары в корзине
		 * 
		 * @param value boolean
		 */
		selectAllItems(value) {
			this.items.forEach((item) => {
				item.isSelected = value;
			});
		},
		
		/*
		Метод, принудительно ревалидирующий форму и возвращающий результат валидации.
		Вызывается перед переходом с текущего этапа на следующий, служит для вывода ошибок заполнения формы пользователю.
		*/
		async isStepReadyToLeave() {
			// до начала валидации скрываем блок с корзиной на форме
			this.isStepLeaveCalculationsInProgress = true;
			await this.reloadBasket();
			// если корзина невалидна, то блок раскрываем обратно
			if (!this.isOrderable)
				this.isStepLeaveCalculationsInProgress = false;
			// иначе не делаем ничего - из блока вышли, вернёмся в него только по методу preload(), блок раскроется в нём

			return this.isOrderable;
		},
		// метод, вызываемый перед переходом на текущий этап
		async preload() {
			// раскрываем блок с корзиной на форме при переходе на этап корзины
			this.isStepLeaveCalculationsInProgress = false;
			await this.reloadBasket();
		},

		// метод, собирающий данные, передаваемые на бэкенд при регистрации заказа
		getOrderData() {
			return {
				items: this.items,
			};
		},

		async changeFavorites(item) {
			await window.BX.ajax.runAction('o2k:sitecore.api.favorites.change', {
				data: {
					productId: item.productId
				}
			})
				.then(
					(response) => {
						const data = response.data;
						if (Object.prototype.hasOwnProperty.call(data, 'error') && data.error) {
							console.error(data.error);
						} else if (Object.prototype.hasOwnProperty.call(data, 'action') && data.action) {
							if (data.action === 'add') {
								item.isFavorite = true;
							} else if (data.action === 'delete') {
								item.isFavorite = false;
							}
						}
					}, 
					(response)=> {
						console.error(response);
					}
				);
		},

		async setBasketItemQuantity(item) {
			await this._callBasketMethod(
				'setItemQuantity',
				{ basketItemId: item.basketId, quantity: item.quantity }
			);
		},

		async deleteBasketItem(item) {
			item.isDeleted = true;
			await this._callBasketMethod(
				'removeItem',
				{ basketItemId: item.basketId }
			);
		},
		
		async removeSelectedItems() {
			if (this.isAllSelected) {
				await this.clearBasket();
			} else {
				const itemsId = this.itemsSelected?.map(i => i.basketId);
				if (itemsId) {
					await this._callBasketMethod(
						'removeSelectedItem',
						{ basketItemsId: itemsId }
					);
				}
			}
		},

		async removeBasketItem(item) {
			item.shouldBeRemoved = true;
			await this.deleteBasketItem(item);
		},

		async replaceBasketItem(item, newProductId) {
			await this._callBasketMethod(
				'replaceBasketItemWithProduct',
				{basketItemId: item.basketId, productId: newProductId, quantity: item.quantity}
			);
		},

		async restoreBasketItem(item) {
			item.isDeleted = false;
			await this._callBasketMethod(
				'addProduct',
				{productId: item.productId, quantity: item.quantity}
			);
		},

		async reloadBasketData() {
			await this._callBasketMethod('getBasket');
		},

		async clearBasket() {
			await this._callBasketMethod('clearBasket');
		},

		async applyCoupon(coupon) {
			await this._callBasketMethod(
				'applyCoupon',
				{coupon: coupon}
			);
		},

		async removeCoupon(coupon) {
			await this._callBasketMethod(
				'removeCoupon',
				{coupon: coupon}
			);
		},

		async resetCoupons() {
			await this._callBasketMethod('resetCoupons');
		},

		_errorNotify(message, errorData = null, error = null) {
			this.eventBus.emit('o2k:order:error', {message: message, errorObject: error, errorData: errorData});
		},

		_outOfStockNotify() {
			this.eventBus.emit('o2k:basketStore:outOfStockSkuError');
		},

		async _callBasketMethod(func, data) {
			this.isLoaded = false;
			await this._sendBasketRequest(func, data);
			this.isLoaded = true;
		},

		async _sendBasketRequest(func, data = {}) {
			try {
				data.returnBasketData = true;
				if (this._isDebugEnabled) {
					console.log('basket request sent', data);
				}
				const response = await window.BX.ajax.runAction(
					`${this._BACKEND_BITRIX_CONTROLLER_NAME}.${func}`,
					{
						data: data
					}
				);
				const responseObject = await response.data;

				if (this._isDebugEnabled) {
					console.log('basket response received', responseObject);
				}

				if (responseObject.success) {
					this.setBasketData(responseObject.basketData);
				} else {
					this._handleBasketRequestError(responseObject);
				}

			} catch (err) {
				const error = err.errors ? err.errors.slice(-1)[0] : err;
				this._errorNotify(error.message, null, error);
			}
		},

		setBasketData(response) {
			const initiator = this._reloadInitiator || 'basket';
			this._reloadInitiator = null;

			this.eventBus.emit(
				'o2k:basketStore:beforeDataUpdate',
				{initiator: initiator, source: response}
			);

			this._updateBasketItems(response);
			this._updateBasketSummary(response);
			this._updateDiscountsData(response);

			if (!this.isInitialized) {
				this.isInitialized = true;
			}
			this.isLoaded = true;

			nextTick(() =>
				this.eventBus.emit(
					'o2k:basketStore:afterDataUpdate',
					{ initiator: initiator, source: response }
				)
			);
		},

		_handleBasketRequestError(response) {
			if (response.basketData) {
				this.setBasketData(response.basketData);
			}

			if (response.code === 'OUT_OF_STOCK') {
				this._outOfStockNotify();
			} else {
				this._errorNotify(response.message, response);
			}
		},

		_updateDiscountsData(response) {
			this.discounts = response.discounts;
			this.coupons = response.coupons;
		},

		_updateBasketItems(response) {
			const basket = response.basketItems;
			// собираем список всех имеющихся товаров в корзине с бэка по их id
			const newBasketItemsMap = {};
			for (const [basketItemId, item] of Object.entries(basket)) {
				newBasketItemsMap[item.basketId] = basketItemId;
			}
			const indexesToDelete = [];
			const indexesOfFrontDeleted = [];
			const indUpdated = [];
			// проверяем, все ли товары из корзины с бэка есть в корзине с фронта, и наоборот
			// плюс собираем список всех имеющихся товаров в корзине на фронте по их id
			const curBasketItemsMap = {};
			this.items.forEach((item, index) => {
				curBasketItemsMap[item.basketId] = index;
				if (newBasketItemsMap[item.basketId] !== undefined) {
					// товар с фронта есть на бэке
				} else {
					// товара с фронта нет на бэке
					if (item.isDeleted && !item.shouldBeRemoved) {
						// товар в корзине висит как удалённый ("вы удалили ... - восстановить")
						// такой товар не удаляем
						indexesOfFrontDeleted.push(index);
						this._triggerBasketItemDeleteEvent(item);
					} else {
						// товар в корзине висит как актуальный
						// фиксируем его на удаление из корзины
						indexesToDelete.push(index);
					}
				}
			});

			// перебираем все товары из корзины с бэка, по их id ищем товар в корзине на фронте
			const itemsToAdd = [];
			for (const [basketItemId, item] of Object.entries(basket)) {
				if (curBasketItemsMap[basketItemId] !== undefined) {
					// товар с бэка есть на фронте
					// перезаписываем фронт бэком
					this._triggerBasketItemUpdateEvent(this.items[curBasketItemsMap[basketItemId]], item);
					this.items[curBasketItemsMap[basketItemId]] = item;
					indUpdated.push(curBasketItemsMap[basketItemId]);
				} else {
					// товара с бэка нет на фронте - добавляем его
					itemsToAdd.push(item);
				}
			}

			const addedItemsToAdd = [];

			// для каждой удалённой на фронте ("вы удалили ... - восстановить") позиции проверяем, не появился ли такой 
			// же товар на беке в виде полноценного товара
			indexesOfFrontDeleted.forEach((index) => {
				const itemToCompare = this.items[index];
				itemsToAdd.forEach((itemToAdd, j) => {
					if (itemToAdd.productId !== itemToCompare.productId) {
						return;
					}
					// если такой товар на беке появился, то заменяем удалённый на фронте добавленным на беке
					const indexToReplace = indexesOfFrontDeleted.shift();
					this.items[indexToReplace] = itemToAdd;
					itemsToAdd.splice(j, 1);
					this._triggerBasketItemAddEvent(itemToAdd);
				});
			});

			if (itemsToAdd.length === 1 && indexesToDelete.length === 1) {
				// если удаляется один товар и один товар добавляется, то заменяем удаляемый новым
				const indexToReplace = indexesToDelete.shift();
				const itemToAdd = itemsToAdd.shift();
				this._triggerBasketItemDeleteEvent(this.items[indexToReplace]);
				this.items[indexToReplace] = itemToAdd;
				this._triggerBasketItemAddEvent(itemToAdd);
			} else {
				// перебираем все новые товары, ищем им соответствия из удаляемых товаров по productId - вдруг у товара
				// сменился basketId
				itemsToAdd.forEach((itemToAdd, i) => {
					indexesToDelete.forEach((index) => {
						const itemToCompare = this.items[index];
						// если для добавляемого товара есть удаляемый товар с таким же productId, то заменяемый удаляемый
						// добавляемым
						if (itemToCompare.productId === itemToAdd.productId) {
							this._triggerBasketItemUpdateEvent(this.items[index], itemToAdd);
							this.items[index] = itemToAdd;
							indUpdated.push(index);
							addedItemsToAdd.push(index);
							itemsToAdd.splice(i, 1);
						}
					});
				});
			}


			// удаляем все товары в списке на удаление...
			let deletionsCount = 0;
			indexesToDelete.forEach((index) => {
				const correctedIndex = index - deletionsCount;
				// ...кроме тех, для которых уже были найдены соответствующие товары с бэка
				if (!indUpdated.includes(correctedIndex)) {
					this._triggerBasketItemDeleteEvent(this.items[correctedIndex]);
					this.items.splice(correctedIndex, 1);
					deletionsCount++;
				}
			});

			// добавляем все товары в списке на добавление.
			itemsToAdd.forEach((itemToAdd) => {
				// ...кроме тех, для которых уже были найдены соответствующие товары с бэка
				if (!addedItemsToAdd.includes(itemToAdd)) {
					this._triggerBasketItemAddEvent(itemToAdd);
					this.items.push(itemToAdd);
				}
			});
			
			this._recalculateIsBasketOrderable();
		},

		_updateBasketSummary(response) {
			this.summary = response.basketSummary;
		},

		_recalculateIsBasketOrderable() {
			this.isOrderable = this.itemsActiveAvailable.length > 0;
			// this.items.forEach((item) => {
			//     if(item.isAvailable && !item.isDeleted && !item.isDelayed && item.quantity > 0)
			//         this.isOrderable = true;
			// });
		},

		_triggerBasketItemAddEvent(basketItem) {
			const eventData = {
				action: 'add',
				isBasketInitialized: this.isInitialized,
				basketId: basketItem.basketId,
				productId: basketItem.productId,
				newState: basketItem
			};

			this.eventBus.emit('o2k:basketStore:basketItemAction', eventData);
			this.eventBus.emit('o2k:basketStore:basketItemAction:' + eventData.productId, eventData);
		},

		_triggerBasketItemUpdateEvent(oldBasketItem, newBasketItem) {
			const eventData = {
				action: 'update',
				isBasketInitialized: this.isInitialized,
				basketId: oldBasketItem.basketId,
				productId: oldBasketItem.productId,
				oldState: oldBasketItem,
				newState: newBasketItem
			};

			this.eventBus.emit('o2k:basketStore:basketItemAction', eventData);
			this.eventBus.emit('o2k:basketStore:basketItemAction:' + eventData.productId, eventData);
		},

		_triggerBasketItemDeleteEvent(basketItem) {
			const eventData = {
				action: 'delete',
				isBasketInitialized: this.isInitialized,
				basketId: basketItem.basketId,
				productId: basketItem.productId,
				oldState: basketItem
			};

			this.eventBus.emit('o2k:basketStore:basketItemAction', eventData);
			this.eventBus.emit('o2k:basketStore:basketItemAction:' + eventData.productId, eventData);
		},
	},
});
